import React from 'react'
import { ExpedienteDespacho } from '../PaginaDespachoPortuario'

interface Props {
  expediente: ExpedienteDespacho
}

export const HojaManifestacionValor: React.FC<Props> = ({ expediente }) => {
  const anio = '26'
  const consecutivo = expediente.folio.replace(/[^0-9]/g, '').slice(-6).padStart(6, '1')
  const folioMVE = `MVE-20${anio}-${consecutivo}`

  const valorComercialUSD = expediente.documentos.factura.valorUSD || 45000
  const tipoCambio = 19.852
  const valorComercialMXN = Math.round(valorComercialUSD * tipoCambio)
  const fleteUSD = expediente.tipoOperacion === 'IMPORTACION' ? 2400 : 0
  const seguroUSD = expediente.tipoOperacion === 'IMPORTACION' ? 350 : 0
  const incrementablesUSD = fleteUSD + seguroUSD
  const totalValorAduanaUSD = valorComercialUSD + incrementablesUSD
  const totalValorAduanaMXN = Math.round(totalValorAduanaUSD * tipoCambio)

  const emisor = expediente.actores.emisor
  const receptor = expediente.actores.receptor

  return (
    <div className="relative w-full bg-[#fdfdfc] text-slate-900 shadow-2xl rounded-sm p-6 sm:p-10 font-sans border border-slate-300 print:border-none print:shadow-none print:p-4 text-[10px] sm:text-[11px] leading-tight select-none">
      {/* Marca de agua */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03] overflow-hidden">
        <div className="text-[90px] font-black uppercase text-slate-900 -rotate-45 select-none tracking-widest whitespace-nowrap">
          VUCEM MVE ART 81
        </div>
      </div>

      {/* Encabezado MVE */}
      <div className="border-b-2 border-slate-900 pb-2 mb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-0.5">
            <p className="text-[8px] font-black uppercase tracking-widest text-slate-600">
              VENTANILLA ÚNICA DE COMERCIO EXTERIOR MEXICANO (VUCEM)
            </p>
            <p className="text-[9px] font-black uppercase tracking-wider text-slate-800">
              SECRETARÍA DE HACIENDA Y CRÉDITO PÚBLICO · SAT · ANAM
            </p>
            <h1 className="text-base sm:text-lg font-black tracking-tight text-slate-950 font-mono">
              MANIFESTACIÓN DE VALOR ELECTRÓNICA (MVE)
            </h1>
            <p className="text-[8px] font-mono text-slate-500">
              FUNDAMENTO: ARTÍCULO 81 DE LA LEY ADUANERA Y REGLA 1.5.1 DE LAS REGLAS GENERALES DE COMERCIO EXTERIOR
            </p>
          </div>

          <div className="text-right flex flex-col items-end">
            <span className="text-[9px] font-bold uppercase bg-indigo-900 text-white px-2.5 py-0.5 rounded-sm font-mono">
              TRANSMISIÓN VUCEM
            </span>
            <span className="font-mono text-[9px] text-slate-700 mt-1 font-bold">
              ACUSE: {folioMVE}
            </span>
            <span className="font-mono text-[8px] text-slate-500">
              ESTADO: FIRMADA CON E.FIRMA
            </span>
          </div>
        </div>
      </div>

      {/* Declaración bajo protesta */}
      <div className="p-2 mb-3 bg-amber-500/10 border border-amber-500/30 rounded text-amber-900 font-serif italic text-[9px] leading-relaxed">
        "Bajo protesta de decir verdad, manifiesto que los datos contenidos en el presente documento son verdaderos, que el precio pagado o por pagar es el efectivamente convenido entre comprador y vendedor y que no existe vinculación que afecte el valor en aduana declarado."
      </div>

      {/* Cuadrícula 1: Datos de las Partes */}
      <div className="border-2 border-slate-900 mb-3 divide-y divide-slate-900">
        <div className="bg-slate-200/80 px-2 py-1 font-bold text-[9px] uppercase tracking-wider text-slate-800">
          1. IDENTIFICACIÓN DEL IMPORTADOR / COMPRADOR (MÉXICO)
        </div>
        <div className="grid grid-cols-12 divide-x divide-slate-900 font-mono text-[9px]">
          <div className="col-span-8 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">RAZÓN SOCIAL / NOMBRE</span>
            <span className="font-bold text-slate-950">{receptor.nombre}</span>
          </div>
          <div className="col-span-4 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">RFC DEL IMPORTADOR</span>
            <span className="font-bold text-slate-950">{receptor.rfc}</span>
          </div>
        </div>

        <div className="bg-slate-200/80 px-2 py-1 font-bold text-[9px] uppercase tracking-wider text-slate-800">
          2. IDENTIFICACIÓN DEL PROVEEDOR / VENDEDOR (ORIGEN)
        </div>
        <div className="grid grid-cols-12 divide-x divide-slate-900 font-mono text-[9px]">
          <div className="col-span-8 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">PROVEEDOR EXTRANJERO</span>
            <span className="font-bold text-slate-950">{emisor.nombre}</span>
          </div>
          <div className="col-span-4 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">TAX ID / IDENTIFICADOR FISCAL</span>
            <span className="font-bold text-slate-950">{emisor.rfc} ({emisor.pais})</span>
          </div>
        </div>
      </div>

      {/* Cuadrícula 2: Método de Valoración e Incrementables */}
      <div className="border-2 border-slate-900 mb-3 divide-y divide-slate-900">
        <div className="bg-slate-200/80 px-2 py-1 font-bold text-[9px] uppercase tracking-wider text-slate-800">
          3. DETERMINACIÓN DEL VALOR EN ADUANA (ARTÍCULOS 64 AL 78-A LEY ADUANERA)
        </div>
        <div className="grid grid-cols-12 divide-x divide-slate-900 font-mono text-[9px]">
          <div className="col-span-6 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">MÉTODO DE VALORACIÓN APLICADO</span>
            <span className="font-bold text-slate-950">MÉTODO 1: VALOR DE TRANSACCIÓN DE LAS MERCANCÍAS</span>
          </div>
          <div className="col-span-3 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">VINCULACIÓN (ART. 68)</span>
            <span className="font-bold text-slate-950">NO EXISTE VINCULACIÓN</span>
          </div>
          <div className="col-span-3 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">INCOTERM APLICABLE</span>
            <span className="font-bold text-slate-950">{expediente.documentos.factura.incoterm || 'FOB Manzanillo'}</span>
          </div>
        </div>

        <div className="grid grid-cols-12 divide-x divide-slate-900 font-mono text-[9px]">
          <div className="col-span-3 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">PRECIO PAGADO (FACTURA USD)</span>
            <span className="font-bold text-slate-950">${valorComercialUSD.toLocaleString()} USD</span>
            <span className="text-[8px] text-slate-500 block">(${valorComercialMXN.toLocaleString()} MXN)</span>
          </div>
          <div className="col-span-3 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">FLETES INCREMENTABLES</span>
            <span className="font-bold text-slate-950">${fleteUSD.toLocaleString()} USD</span>
          </div>
          <div className="col-span-3 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">SEGUROS INCREMENTABLES</span>
            <span className="font-bold text-slate-950">${seguroUSD.toLocaleString()} USD</span>
          </div>
          <div className="col-span-3 p-1.5 bg-slate-100/80">
            <span className="block text-[8px] font-sans font-bold text-slate-600">VALOR EN ADUANA TOTAL</span>
            <span className="font-black text-xs text-indigo-900">${totalValorAduanaUSD.toLocaleString()} USD</span>
            <span className="text-[8px] font-bold text-indigo-700 block">${totalValorAduanaMXN.toLocaleString()} MXN</span>
          </div>
        </div>
      </div>

      {/* Sello Digital e.firma VUCEM */}
      <div className="border-2 border-slate-900 p-2 bg-slate-50/50 font-mono text-[8px] space-y-1">
        <p className="font-bold text-slate-800 text-[9px]">SELLO DIGITAL DE TRANSMISIÓN DE E.FIRMA (SAT / VUCEM):</p>
        <p className="break-all text-slate-600 leading-tight">
          ||{folioMVE}|{receptor.rfc}|{valorComercialUSD}|{totalValorAduanaUSD}|2026-10-04T10:14:22Z|d8b1e4f901ac88e99bc104fa221049bc88a104f29104||
        </p>
        <p className="text-slate-500 pt-1 border-t border-slate-300">
          Documento anexo al expediente digital del pedimento conforme al Artículo 81 de la Ley Aduanera vigente en 2026.
        </p>
      </div>
    </div>
  )
}
